import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { AppLayout } from "@/components/AppLayout";
import { SocialShareModal } from "@/components/SocialShareModal";
import {
  Share2, Search, Loader2, Sparkles, BookOpen,
  Bookmark, Eye, MessageSquare, ExternalLink, Filter
} from "lucide-react";
import { cn } from "@/lib/utils";

const TYPE_LABELS = { bookmark: "Bookmarked", shared: "Shared", notable: "Notable" };
const TYPE_COLORS = {
  bookmark: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400",
  shared: "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400",
  notable: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400",
};

const MODULE_LABELS = {
  friend: "Friend", psychologist: "Psychologist", mentor: "Mentor",
  social_guide: "Social Guide", job_guardian: "Job Guardian",
  life_coach: "Life Coach", storyteller: "Storyteller",
  philosopher_king: "Philosopher", creative_muse: "Creative Muse",
  executive_advisor: "Executive", wisdom_roundtable: "Roundtable",
};

export default function SharePortalPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState(null);
  const [shareItem, setShareItem] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/share/portal`, { headers });
      setItems(data.items || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load share portal");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let list = items;
    if (typeFilter) list = list.filter(i => i.type === typeFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(i =>
        i.content?.toLowerCase().includes(q) ||
        i.conversation_title?.toLowerCase().includes(q) ||
        i.wisdom_figure?.toLowerCase().includes(q) ||
        MODULE_LABELS[i.module]?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [items, search, typeFilter]);

  const typeCounts = useMemo(() => {
    const counts = { bookmark: 0, shared: 0, notable: 0 };
    items.forEach(i => { if (counts[i.type] !== undefined) counts[i.type]++; });
    return counts;
  }, [items]);

  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8 space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold flex items-center gap-2.5">
              <Share2 className="w-6 h-6 text-primary" /> Share Portal
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Your best Omni moments — browse, curate, and share with the world.
            </p>
          </div>

          {/* Search + Filters */}
          <div className="space-y-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="portal-search"
                type="text"
                placeholder="Search insights..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-2xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              <button
                data-testid="portal-filter-all"
                onClick={() => setTypeFilter(null)}
                className={cn(
                  "px-3 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap",
                  !typeFilter ? "bg-foreground text-background border-foreground" : "border-border/50 text-muted-foreground hover:bg-muted/50"
                )}
              >
                All ({items.length})
              </button>
              {Object.entries(typeCounts).map(([type, count]) => count > 0 && (
                <button
                  key={type}
                  data-testid={`portal-filter-${type}`}
                  onClick={() => setTypeFilter(typeFilter === type ? null : type)}
                  className={cn(
                    "px-3 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap",
                    typeFilter === type ? "bg-foreground text-background border-foreground" : "border-border/50 text-muted-foreground hover:bg-muted/50"
                  )}
                >
                  {TYPE_LABELS[type]} ({count})
                </button>
              ))}
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Items Grid */}
          {!loading && filtered.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {filtered.map(item => (
                <div
                  key={item.id}
                  data-testid={`portal-item-${item.id}`}
                  className="bg-card border border-border/50 rounded-2xl p-4 sm:p-5 hover:border-primary/20 transition-all group"
                >
                  {/* Top badges */}
                  <div className="flex items-center gap-1.5 mb-3 flex-wrap">
                    <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-semibold", TYPE_COLORS[item.type])}>
                      {TYPE_LABELS[item.type]}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-muted/50 text-[10px] font-medium text-muted-foreground">
                      {MODULE_LABELS[item.module] || item.module}
                    </span>
                    {item.wisdom_figure && (
                      <span className="px-2 py-0.5 rounded-full bg-amber-100/50 dark:bg-amber-900/20 text-[10px] font-medium text-amber-700 dark:text-amber-400">
                        {item.wisdom_figure}
                      </span>
                    )}
                    {item.views !== undefined && (
                      <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground/60">
                        <Eye className="w-3 h-3" /> {item.views}
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <p className="text-sm text-foreground/80 leading-relaxed line-clamp-4 mb-3">
                    {item.content}
                  </p>

                  {/* Subtitle */}
                  {item.conversation_title && (
                    <p className="text-[10px] text-muted-foreground/50 truncate mb-3">
                      {item.conversation_title}
                    </p>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`portal-share-${item.id}`}
                      onClick={() => setShareItem(item)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors"
                    >
                      <Share2 className="w-3.5 h-3.5" /> Share
                    </button>
                    {item.conversation_id && (
                      <button
                        data-testid={`portal-goto-${item.id}`}
                        onClick={() => navigate(`/chat/${item.conversation_id}`)}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                      >
                        <ExternalLink className="w-3 h-3" /> View
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty */}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-16">
              <Share2 className="w-10 h-10 text-muted-foreground/15 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {items.length === 0
                  ? "No shareable moments yet. Chat with Omni and bookmark the best replies!"
                  : `No results for "${search}"`}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Share Modal */}
      {shareItem && (
        <SocialShareModal
          content={shareItem.content}
          module={shareItem.module}
          wisdomFigure={shareItem.wisdom_figure}
          conversationTitle={shareItem.conversation_title}
          onClose={() => setShareItem(null)}
        />
      )}
    </AppLayout>
  );
}
